import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ErrorHandlingService } from "../../shared/error-handling/error-handling.service";
import { API_ENDPOINTS } from "../../insight-egrc-api-endpoints";

export interface RiskRegister {
  id?: string;
  assessmentId?: string;
  organizationId?: string;
  assetId?: string;
  assessmentName?: string;
  description?: string;
  status?: string;
  risk?: number;
  title?: string;
  riskValue?: number;
  [key: string]: any;
}

export interface RiskRegisterOrganization {
  organizationId: string;
  organizationName: string;
  [key: string]: any;
}

export interface RiskRegisterAsset {
  assetId: string;
  organizationId: string;
  assetName: string;
  description?: string;
  status?: string;
  [key: string]: any;
}

export interface ThreatEventFrequency {
  tefId?: string;
  assessmentId?: string;
  organizationId?: string;
  contactFrequency?: number;
  probabilityOfAction?: number;
  timeframe?: string;
  threatAgent?: string;
  tefResult?: number;
  [key: string]: any;
}

export interface Vulnerability {
  vulnerabilityId?: string;
  assessmentId?: string;
  organizationId?: string;
  method?: string;
  threatCapability?: number;
  resistanceStrength?: number;
  vulnerabilityResult?: number;
  [key: string]: any;
}

export interface LossEventFrequency {
  lefId?: string;
  assessmentId?: string;
  organizationId?: string;
  tef?: number;
  vulnerability?: number;
  timeframe?: string;
  lefResult?: number;
  [key: string]: any;
}

export interface LossMagnitude {
  lmId?: string;
  assessmentId?: string;
  organizationId?: string;
  productivityLoss?: number;
  responseCost?: number;
  replacementCost?: number;
  fines?: number;
  competitiveLoss?: number;
  reputationLoss?: number;
  primaryLoss?: number;
  secondaryLoss?: number;
  totalLossMagnitude?: number;
  currency?: string;
  [key: string]: any;
}

export interface AssetLossFactors {
  alfId?: string;
  assessmentId?: string;
  organizationId?: string;
  criticality?: number;
  replacementCost?: number;
  recoveryCost?: number;
  rebuildCost?: number;
  sensitivityScore?: number;
  reputationScore?: number;
  legalExposure?: number;
  competitiveValue?: number;
  volume?: number;
  [key: string]: any;
}

export interface ThreatLossFactors {
  tlfId?: string;
  assessmentId?: string;
  organizationId?: string;
  threatAction?: string;
  threatCompetence?: number;
  threatOrigin?: string;
  tlfScore?: number;
  [key: string]: any;
}

export interface OrganizationalLossFactors {
  olfId?: string;
  assessmentId?: string;
  organizationId?: string;
  timingScore?: number;
  careScore?: number;
  detectionScore?: number;
  containmentScore?: number;
  remediationScore?: number;
  recoveryScore?: number;
  olfScore?: number;
  [key: string]: any;
}

export interface ExternalLossFactors {
  elfId?: string;
  assessmentId?: string;
  organizationId?: string;
  externalDetection?: number;
  regulatoryExposure?: number;
  competitorScore?: number;
  mediaScore?: number;
  stakeholderScore?: number;
  elfScore?: number;
  [key: string]: any;
}

interface GetRiskRegistersResponse {
  riskRegisters: RiskRegister[];
}

interface GetRiskRegisterResponse {
  riskRegister: RiskRegister;
}

interface GetRiskRegisterOrganizationsResponse {
  organizations: RiskRegisterOrganization[];
}

interface GetRiskRegisterAssetsResponse {
  assets: RiskRegisterAsset[];
}

interface SaveRiskRegisterResponse {
  assessmentId?: string;
  [key: string]: any;
}

interface GetThreatEventFrequencyResponse {
  threatEventFrequency: ThreatEventFrequency | null;
}

interface SaveThreatEventFrequencyResponse {
  tefId?: string;
  [key: string]: any;
}

interface GetVulnerabilityResponse {
  vulnerability: Vulnerability | null;
}

interface SaveVulnerabilityResponse {
  vulnerabilityId?: string;
  [key: string]: any;
}

interface GetLossEventFrequencyResponse {
  lossEventFrequency: LossEventFrequency | null;
}

interface SaveLossEventFrequencyResponse {
  lefId?: string;
  [key: string]: any;
}

interface GetAssetLossFactorsResponse {
  assetLossFactors: AssetLossFactors | null;
}

interface SaveAssetLossFactorsResponse {
  alfId?: string;
  [key: string]: any;
}

interface GetThreatLossFactorsResponse {
  threatLossFactors: ThreatLossFactors | null;
}

interface SaveThreatLossFactorsResponse {
  tlfId?: string;
  [key: string]: any;
}

interface GetOrganizationalLossFactorsResponse {
  organizationalLossFactors: OrganizationalLossFactors | null;
}

interface SaveOrganizationalLossFactorsResponse {
  olfId?: string;
  [key: string]: any;
}

interface GetExternalLossFactorsResponse {
  externalLossFactors: ExternalLossFactors | null;
}

interface SaveExternalLossFactorsResponse {
  elfId?: string;
  [key: string]: any;
}

interface GetLossMagnitudeResponse {
  lossMagnitude: LossMagnitude | null;
}

interface SaveLossMagnitudeResponse {
  lmId?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: "root",
})
export class RiskRegisterService {
  private readonly apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService,
  ) {}

  getRiskRegister(
    currentPage: number,
    sessionId: string,
    userId: string,
  ): Observable<RiskRegister[]> {
    const headers = {
      sessionId: sessionId,
      userId: userId,
    };

    return this.http
      .post<GetRiskRegistersResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.FETCH}`,
        {
          currentPage,
        },
        {
          headers: headers, // Add the custom headers
        },
      )
      .pipe(
        map((response) =>
          response.riskRegisters.map((risk) => this.normalizeRiskRegister(risk)),
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getRiskRegisterSearch(
    currentPage: number,
    searchText: String,
    filterByHeader: String,
  ): Observable<RiskRegister[]> {
    return this.http
      .post<GetRiskRegistersResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.SEARCH}`,
        {
          currentPage,
          searchText,
          filterByHeader,
        },
      )
      .pipe(
        map((response) =>
          response.riskRegisters.map((risk) => this.normalizeRiskRegister(risk)),
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getRiskRegisters(riskRegisterId: string): Observable<RiskRegister> {
    return this.http
      .post<GetRiskRegisterResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.GET}`,
        {
          riskRegisterId,
        },
      )
      .pipe(
        map((response) => this.normalizeRiskRegister(response.riskRegister)),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getOrganizations(): Observable<RiskRegisterOrganization[]> {
    return this.http
      .post<GetRiskRegisterOrganizationsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ORGANIZATIONS}`,
        {},
      )
      .pipe(
        map((response) =>
          (response.organizations ?? [])
            .map((organization) => this.normalizeOrganization(organization))
            .filter(
              (organization) =>
                !!organization.organizationId &&
                !!organization.organizationName,
            ),
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getAssets(organizationId?: string): Observable<RiskRegisterAsset[]> {
    return this.http
      .post<GetRiskRegisterAssetsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ASSETS}`,
        organizationId ? { organizationId } : {},
      )
      .pipe(
        map((response) =>
          (response.assets ?? [])
            .map((asset) => this.normalizeAsset(asset))
            .filter(
              (asset) => !!asset.assetId && !!asset.organizationId && !!asset.assetName,
            ),
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createRiskRegister(
    risk: Partial<RiskRegister>,
  ): Observable<SaveRiskRegisterResponse> {
    return this.http
      .post<SaveRiskRegisterResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.CREATE}`,
        {
        ...risk,
      },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateRiskRegister(id: string, risk: Partial<RiskRegister>): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.UPDATE}`, {
        id,
        ...risk,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  deleteRiskRegisterl(riskRegisterId: string): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.DELETE}`, {
        riskRegisterId,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getThreatEventFrequency(
    assessmentId: string,
  ): Observable<ThreatEventFrequency | null> {
    return this.http
      .post<GetThreatEventFrequencyResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.TEF_GET}`,
        {
          assessmentId,
        },
      )
      .pipe(
        map((response) =>
          response.threatEventFrequency
            ? this.normalizeThreatEventFrequency(response.threatEventFrequency)
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createThreatEventFrequency(
    threatEventFrequency: Partial<ThreatEventFrequency>,
  ): Observable<SaveThreatEventFrequencyResponse> {
    return this.http
      .post<SaveThreatEventFrequencyResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.TEF_CREATE}`,
        {
          ...threatEventFrequency,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateThreatEventFrequency(
    tefId: string,
    threatEventFrequency: Partial<ThreatEventFrequency>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.TEF_UPDATE}`, {
        tefId,
        ...threatEventFrequency,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getVulnerability(assessmentId: string): Observable<Vulnerability | null> {
    return this.http
      .post<GetVulnerabilityResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.VULNERABILITY_GET}`,
        {
          assessmentId,
        },
      )
      .pipe(
        map((response) =>
          response.vulnerability
            ? this.normalizeVulnerability(response.vulnerability)
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createVulnerability(
    vulnerability: Partial<Vulnerability>,
  ): Observable<SaveVulnerabilityResponse> {
    return this.http
      .post<SaveVulnerabilityResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.VULNERABILITY_CREATE}`,
        {
          ...vulnerability,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateVulnerability(
    vulnerabilityId: string,
    vulnerability: Partial<Vulnerability>,
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.VULNERABILITY_UPDATE}`,
        {
          vulnerabilityId,
          ...vulnerability,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getLossEventFrequency(
    assessmentId: string,
  ): Observable<LossEventFrequency | null> {
    return this.http
      .post<GetLossEventFrequencyResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.LEF_GET}`,
        {
          assessmentId,
        },
      )
      .pipe(
        map((response) =>
          response.lossEventFrequency
            ? this.normalizeLossEventFrequency(response.lossEventFrequency)
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createLossEventFrequency(
    lossEventFrequency: Partial<LossEventFrequency>,
  ): Observable<SaveLossEventFrequencyResponse> {
    return this.http
      .post<SaveLossEventFrequencyResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.LEF_CREATE}`,
        {
          ...lossEventFrequency,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateLossEventFrequency(
    lefId: string,
    lossEventFrequency: Partial<LossEventFrequency>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.LEF_UPDATE}`, {
        lefId,
        ...lossEventFrequency,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getAssetLossFactors(assessmentId: string): Observable<AssetLossFactors | null> {
    return this.http
      .post<GetAssetLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ALF_GET}`,
        {
          assessmentId,
        },
      )
      .pipe(
        map((response) =>
          response.assetLossFactors
            ? this.normalizeAssetLossFactors(response.assetLossFactors)
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createAssetLossFactors(
    assetLossFactors: Partial<AssetLossFactors>,
  ): Observable<SaveAssetLossFactorsResponse> {
    return this.http
      .post<SaveAssetLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ALF_CREATE}`,
        {
          ...assetLossFactors,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateAssetLossFactors(
    alfId: string,
    assetLossFactors: Partial<AssetLossFactors>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ALF_UPDATE}`, {
        alfId,
        ...assetLossFactors,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getThreatLossFactors(
    assessmentId: string,
  ): Observable<ThreatLossFactors | null> {
    return this.http
      .post<GetThreatLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.TLF_GET}`,
        {
          assessmentId,
        },
      )
      .pipe(
        map((response) =>
          response.threatLossFactors
            ? this.normalizeThreatLossFactors(response.threatLossFactors)
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createThreatLossFactors(
    threatLossFactors: Partial<ThreatLossFactors>,
  ): Observable<SaveThreatLossFactorsResponse> {
    return this.http
      .post<SaveThreatLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.TLF_CREATE}`,
        {
          ...threatLossFactors,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateThreatLossFactors(
    tlfId: string,
    threatLossFactors: Partial<ThreatLossFactors>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.TLF_UPDATE}`, {
        tlfId,
        ...threatLossFactors,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getOrganizationalLossFactors(
    assessmentId: string,
  ): Observable<OrganizationalLossFactors | null> {
    return this.http
      .post<GetOrganizationalLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.OLF_GET}`,
        {
          assessmentId,
        },
      )
      .pipe(
        map((response) =>
          response.organizationalLossFactors
            ? this.normalizeOrganizationalLossFactors(
                response.organizationalLossFactors,
              )
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createOrganizationalLossFactors(
    organizationalLossFactors: Partial<OrganizationalLossFactors>,
  ): Observable<SaveOrganizationalLossFactorsResponse> {
    return this.http
      .post<SaveOrganizationalLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.OLF_CREATE}`,
        {
          ...organizationalLossFactors,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateOrganizationalLossFactors(
    olfId: string,
    organizationalLossFactors: Partial<OrganizationalLossFactors>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.OLF_UPDATE}`, {
        olfId,
        ...organizationalLossFactors,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getExternalLossFactors(
    assessmentId: string,
  ): Observable<ExternalLossFactors | null> {
    return this.http
      .post<GetExternalLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ELF_GET}`,
        {
          assessmentId,
        },
      )
      .pipe(
        map((response) =>
          response.externalLossFactors
            ? this.normalizeExternalLossFactors(response.externalLossFactors)
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createExternalLossFactors(
    externalLossFactors: Partial<ExternalLossFactors>,
  ): Observable<SaveExternalLossFactorsResponse> {
    return this.http
      .post<SaveExternalLossFactorsResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ELF_CREATE}`,
        {
          ...externalLossFactors,
        },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateExternalLossFactors(
    elfId: string,
    externalLossFactors: Partial<ExternalLossFactors>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.ELF_UPDATE}`, {
        elfId,
        ...externalLossFactors,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  getLossMagnitude(assessmentId: string): Observable<LossMagnitude | null> {
    return this.http
      .post<GetLossMagnitudeResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.LM_GET}`,
        { assessmentId },
      )
      .pipe(
        map((response) =>
          response.lossMagnitude
            ? this.normalizeLossMagnitude(response.lossMagnitude)
            : null,
        ),
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  createLossMagnitude(
    lossMagnitude: Partial<LossMagnitude>,
  ): Observable<SaveLossMagnitudeResponse> {
    return this.http
      .post<SaveLossMagnitudeResponse>(
        `${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.LM_CREATE}`,
        { ...lossMagnitude },
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  updateLossMagnitude(
    lmId: string,
    lossMagnitude: Partial<LossMagnitude>,
  ): Observable<any> {
    return this.http
      .post<any>(`${this.apiUrl}${API_ENDPOINTS.RISKREGISTER.LM_UPDATE}`, {
        lmId,
        ...lossMagnitude,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error)),
      );
  }

  private normalizeRiskRegister(risk: any): RiskRegister {
    if (!risk) {
      return risk;
    }

    const assessmentId =
      risk.assessmentId ??
      risk.assessment_id ??
      risk.id ??
      risk.riskRegisterId;
    const assessmentName =
      risk.assessmentName ?? risk.assessment_name ?? risk.title;
    const riskValue = risk.risk ?? risk.riskValue;

    return {
      ...risk,
      id: assessmentId,
      assessmentId,
      organizationId: risk.organizationId ?? risk.organization_id,
      assetId: risk.assetId ?? risk.asset_id,
      assessmentName,
      title: assessmentName,
      description: risk.description,
      status: risk.status,
      risk: riskValue != null ? Number(riskValue) : undefined,
      riskValue: riskValue != null ? Number(riskValue) : undefined,
    };
  }

  private normalizeOrganization(
    organization: any,
  ): RiskRegisterOrganization {
    return {
      ...organization,
      organizationId:
        organization.organizationId ??
        organization.organizationid ??
        organization.organization_id ??
        organization.id ??
        "",
      organizationName:
        organization.organizationName ??
        organization.organizationname ??
        organization.organization_name ??
        organization.name ??
        organization.title ??
        "",
    };
  }

  private normalizeAsset(asset: any): RiskRegisterAsset {
    return {
      ...asset,
      assetId: asset.assetId ?? asset.assetid ?? asset.asset_id ?? asset.id ?? "",
      organizationId:
        asset.organizationId ??
        asset.organizationid ??
        asset.organization_id ??
        "",
      assetName:
        asset.assetName ??
        asset.assetname ??
        asset.asset_name ??
        asset.name ??
        asset.title ??
        "",
      description: asset.description,
      status: asset.status,
    };
  }

  private normalizeThreatEventFrequency(
    threatEventFrequency: any,
  ): ThreatEventFrequency {
    return {
      ...threatEventFrequency,
      tefId:
        threatEventFrequency.tefId ??
        threatEventFrequency.tefid ??
        threatEventFrequency.tef_id ??
        threatEventFrequency.id,
      assessmentId:
        threatEventFrequency.assessmentId ??
        threatEventFrequency.assessmentid ??
        threatEventFrequency.assessment_id,
      organizationId:
        threatEventFrequency.organizationId ??
        threatEventFrequency.organizationid ??
        threatEventFrequency.organization_id,
      contactFrequency:
        threatEventFrequency.contactFrequency ??
        threatEventFrequency.contactfrequency ??
        threatEventFrequency.contact_frequency,
      probabilityOfAction:
        threatEventFrequency.probabilityOfAction ??
        threatEventFrequency.probabilityofaction ??
        threatEventFrequency.probability_of_action,
      timeframe:
        threatEventFrequency.timeframe ?? "",
      threatAgent:
        threatEventFrequency.threatAgent ??
        threatEventFrequency.threatagent ??
        threatEventFrequency.threat_agent ??
        "",
      tefResult:
        threatEventFrequency.tefResult ??
        threatEventFrequency.tefresult ??
        threatEventFrequency.tef_result,
    };
  }

  private normalizeVulnerability(vulnerability: any): Vulnerability {
    return {
      ...vulnerability,
      vulnerabilityId:
        vulnerability.vulnerabilityId ??
        vulnerability.vulnerabilityid ??
        vulnerability.vulnerability_id ??
        vulnerability.id,
      assessmentId:
        vulnerability.assessmentId ??
        vulnerability.assessmentid ??
        vulnerability.assessment_id,
      organizationId:
        vulnerability.organizationId ??
        vulnerability.organizationid ??
        vulnerability.organization_id,
      method: vulnerability.method ?? "",
      threatCapability:
        vulnerability.threatCapability ??
        vulnerability.threatcapability ??
        vulnerability.threat_capability,
      resistanceStrength:
        vulnerability.resistanceStrength ??
        vulnerability.resistancestrength ??
        vulnerability.resistance_strength,
      vulnerabilityResult:
        vulnerability.vulnerabilityResult ??
        vulnerability.vulnerabilityresult ??
        vulnerability.vulnerability_result,
    };
  }

  private normalizeLossEventFrequency(
    lossEventFrequency: any,
  ): LossEventFrequency {
    return {
      ...lossEventFrequency,
      lefId:
        lossEventFrequency.lefId ??
        lossEventFrequency.lefid ??
        lossEventFrequency.lef_id ??
        lossEventFrequency.id,
      assessmentId:
        lossEventFrequency.assessmentId ??
        lossEventFrequency.assessmentid ??
        lossEventFrequency.assessment_id,
      organizationId:
        lossEventFrequency.organizationId ??
        lossEventFrequency.organizationid ??
        lossEventFrequency.organization_id,
      tef:
        lossEventFrequency.tef,
      vulnerability:
        lossEventFrequency.vulnerability,
      timeframe:
        lossEventFrequency.timeframe ?? "",
      lefResult:
        lossEventFrequency.lefResult ??
        lossEventFrequency.lefresult ??
        lossEventFrequency.lef_result,
    };
  }

  private normalizeAssetLossFactors(assetLossFactors: any): AssetLossFactors {
    return {
      ...assetLossFactors,
      alfId:
        assetLossFactors.alfId ??
        assetLossFactors.alfid ??
        assetLossFactors.alf_id ??
        assetLossFactors.id,
      assessmentId:
        assetLossFactors.assessmentId ??
        assetLossFactors.assessmentid ??
        assetLossFactors.assessment_id,
      organizationId:
        assetLossFactors.organizationId ??
        assetLossFactors.organizationid ??
        assetLossFactors.organization_id,
      criticality: assetLossFactors.criticality,
      replacementCost:
        assetLossFactors.replacementCost ??
        assetLossFactors.replacementcost ??
        assetLossFactors.replacement_cost,
      recoveryCost:
        assetLossFactors.recoveryCost ??
        assetLossFactors.recoverycost ??
        assetLossFactors.recovery_cost,
      rebuildCost:
        assetLossFactors.rebuildCost ??
        assetLossFactors.rebuildcost ??
        assetLossFactors.rebuild_cost,
      sensitivityScore:
        assetLossFactors.sensitivityScore ??
        assetLossFactors.sensitivityscore ??
        assetLossFactors.sensitivity_score,
      reputationScore:
        assetLossFactors.reputationScore ??
        assetLossFactors.reputationscore ??
        assetLossFactors.reputation_score,
      legalExposure:
        assetLossFactors.legalExposure ??
        assetLossFactors.legalexposure ??
        assetLossFactors.legal_exposure,
      competitiveValue:
        assetLossFactors.competitiveValue ??
        assetLossFactors.competitivevalue ??
        assetLossFactors.competitive_value,
      volume: assetLossFactors.volume,
    };
  }

  private normalizeThreatLossFactors(threatLossFactors: any): ThreatLossFactors {
    return {
      ...threatLossFactors,
      tlfId:
        threatLossFactors.tlfId ??
        threatLossFactors.tlfid ??
        threatLossFactors.tlf_id ??
        threatLossFactors.id,
      assessmentId:
        threatLossFactors.assessmentId ??
        threatLossFactors.assessmentid ??
        threatLossFactors.assessment_id,
      organizationId:
        threatLossFactors.organizationId ??
        threatLossFactors.organizationid ??
        threatLossFactors.organization_id,
      threatAction:
        threatLossFactors.threatAction ??
        threatLossFactors.threataction ??
        threatLossFactors.threat_action ??
        "",
      threatCompetence:
        threatLossFactors.threatCompetence ??
        threatLossFactors.threatcompetence ??
        threatLossFactors.threat_competence,
      threatOrigin:
        threatLossFactors.threatOrigin ??
        threatLossFactors.threatorigin ??
        threatLossFactors.threat_origin ??
        "",
      tlfScore:
        threatLossFactors.tlfScore ??
        threatLossFactors.tlfscore ??
        threatLossFactors.tlf_score,
    };
  }

  private normalizeOrganizationalLossFactors(
    organizationalLossFactors: any,
  ): OrganizationalLossFactors {
    return {
      ...organizationalLossFactors,
      olfId:
        organizationalLossFactors.olfId ??
        organizationalLossFactors.olfid ??
        organizationalLossFactors.olf_id ??
        organizationalLossFactors.id,
      assessmentId:
        organizationalLossFactors.assessmentId ??
        organizationalLossFactors.assessmentid ??
        organizationalLossFactors.assessment_id,
      organizationId:
        organizationalLossFactors.organizationId ??
        organizationalLossFactors.organizationid ??
        organizationalLossFactors.organization_id,
      timingScore:
        organizationalLossFactors.timingScore ??
        organizationalLossFactors.timingscore ??
        organizationalLossFactors.timing_score,
      careScore:
        organizationalLossFactors.careScore ??
        organizationalLossFactors.carescore ??
        organizationalLossFactors.care_score,
      detectionScore:
        organizationalLossFactors.detectionScore ??
        organizationalLossFactors.detectionscore ??
        organizationalLossFactors.detection_score,
      containmentScore:
        organizationalLossFactors.containmentScore ??
        organizationalLossFactors.containmentscore ??
        organizationalLossFactors.containment_score,
      remediationScore:
        organizationalLossFactors.remediationScore ??
        organizationalLossFactors.remediationscore ??
        organizationalLossFactors.remediation_score,
      recoveryScore:
        organizationalLossFactors.recoveryScore ??
        organizationalLossFactors.recoveryscore ??
        organizationalLossFactors.recovery_score,
      olfScore:
        organizationalLossFactors.olfScore ??
        organizationalLossFactors.olfscore ??
        organizationalLossFactors.olf_score,
    };
  }

  private normalizeExternalLossFactors(
    externalLossFactors: any,
  ): ExternalLossFactors {
    return {
      ...externalLossFactors,
      elfId:
        externalLossFactors.elfId ??
        externalLossFactors.elfid ??
        externalLossFactors.elf_id ??
        externalLossFactors.id,
      assessmentId:
        externalLossFactors.assessmentId ??
        externalLossFactors.assessmentid ??
        externalLossFactors.assessment_id,
      organizationId:
        externalLossFactors.organizationId ??
        externalLossFactors.organizationid ??
        externalLossFactors.organization_id,
      externalDetection:
        externalLossFactors.externalDetection ??
        externalLossFactors.externaldetection ??
        externalLossFactors.external_detection,
      regulatoryExposure:
        externalLossFactors.regulatoryExposure ??
        externalLossFactors.regulatoryexposure ??
        externalLossFactors.regulatory_exposure,
      competitorScore:
        externalLossFactors.competitorScore ??
        externalLossFactors.competitorscore ??
        externalLossFactors.competitor_score,
      mediaScore:
        externalLossFactors.mediaScore ??
        externalLossFactors.mediascore ??
        externalLossFactors.media_score,
      stakeholderScore:
        externalLossFactors.stakeholderScore ??
        externalLossFactors.stakeholderscore ??
        externalLossFactors.stakeholder_score,
      elfScore:
        externalLossFactors.elfScore ??
        externalLossFactors.elfscore ??
        externalLossFactors.elf_score,
    };
  }

  private normalizeLossMagnitude(lossMagnitude: any): LossMagnitude {
    return {
      ...lossMagnitude,
      lmId:
        lossMagnitude.lmId ??
        lossMagnitude.lmid ??
        lossMagnitude.lm_id ??
        lossMagnitude.id,
      assessmentId:
        lossMagnitude.assessmentId ??
        lossMagnitude.assessmentid ??
        lossMagnitude.assessment_id,
      organizationId:
        lossMagnitude.organizationId ??
        lossMagnitude.organizationid ??
        lossMagnitude.organization_id,
      productivityLoss:
        lossMagnitude.productivityLoss ??
        lossMagnitude.productivityloss ??
        lossMagnitude.productivity_loss,
      responseCost:
        lossMagnitude.responseCost ??
        lossMagnitude.responsecost ??
        lossMagnitude.response_cost,
      replacementCost:
        lossMagnitude.replacementCost ??
        lossMagnitude.replacementcost ??
        lossMagnitude.replacement_cost,
      fines: lossMagnitude.fines,
      competitiveLoss:
        lossMagnitude.competitiveLoss ??
        lossMagnitude.competitiveloss ??
        lossMagnitude.competitive_loss,
      reputationLoss:
        lossMagnitude.reputationLoss ??
        lossMagnitude.reputationloss ??
        lossMagnitude.reputation_loss,
      primaryLoss:
        lossMagnitude.primaryLoss ??
        lossMagnitude.primaryloss ??
        lossMagnitude.primary_loss,
      secondaryLoss:
        lossMagnitude.secondaryLoss ??
        lossMagnitude.secondaryloss ??
        lossMagnitude.secondary_loss,
      totalLossMagnitude:
        lossMagnitude.totalLossMagnitude ??
        lossMagnitude.totallossmagnitude ??
        lossMagnitude.total_loss_magnitude,
      currency: lossMagnitude.currency ?? "USD",
    };
  }
}
